import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { AccountCommandError } from '@/services/vault/accountEnrollment'
import { X } from 'lucide-react'
import type {
  CreatePlatformTenant,
  CreatePlatformUser,
  PlatformTenant,
  PlatformUser,
} from '@/services/platform/platform.http'
import { CANONICAL_ROLES, getRoleLabel, type CanonicalUserRole } from '@/security/accessContract'
import { PasswordField } from '@/components/ui/PasswordField'

function Dialog({
  title,
  description,
  children,
  onClose,
}: {
  title: string
  description: string
  children: ReactNode
  onClose: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <section className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-surface-dark">
        <header className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">{title}</h2>
            <p className="text-sm text-text-secondary">{description}</p>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </header>
        {children}
      </section>
    </div>
  )
}

function ErrorMessage({ error }: { error: string }) {
  return error ? (
    <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
      {error}
    </div>
  ) : null
}

export function CreateTenantDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (body: CreatePlatformTenant) => Promise<void>
}) {
  const [form, setForm] = useState<CreatePlatformTenant>(() => ({
    firm_name: '',
    tenant_type: 'solo_ca',
    plan_code: 'trial',
    owner_email: '',
    owner_name: '',
    request_id: crypto.randomUUID(),
    command_id: crypto.randomUUID(),
    bootstrap_secret: Array.from(crypto.getRandomValues(new Uint8Array(32)), value =>
      value.toString(16).padStart(2, '0')
    ).join(''),
  }))
  const pending = useRef<CreatePlatformTenant | null>(null)
  const running = useRef(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const change = (key: keyof CreatePlatformTenant, value: string) => {
    setForm(current => {
      const updated = { ...current, [key]: value }
      // Auto-fill firm email from owner email when firm email hasn't been manually set
      if (
        key === 'owner_email' &&
        (!current.firm_email || current.firm_email === current.owner_email)
      ) {
        updated.firm_email = value
      }
      return updated
    })
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (running.current) return
    running.current = true
    setSaving(true)
    setError('')
    const body = pending.current ?? { ...form }
    try {
      await onCreate(body)
      pending.current = null
      onClose()
    } catch (value) {
      if (value instanceof AccountCommandError && value.uncertain) pending.current = body
      setError(value instanceof Error ? value.message : 'Tenant creation failed')
    } finally {
      running.current = false
      setSaving(false)
    }
  }
  return (
    <Dialog
      title="Create tenant"
      description="Request a pending firm. The owner completes email and authenticator verification before activation."
      onClose={onClose}
    >
      <form onSubmit={event => void submit(event)}>
        <ErrorMessage error={error} />
        <fieldset disabled={saving || !!pending.current} className="grid gap-4 md:grid-cols-2">
          <label className="text-sm md:col-span-2">
            Organization name
            <input
              required
              minLength={2}
              className="input mt-1 w-full"
              value={form.firm_name}
              onChange={e => change('firm_name', e.target.value)}
            />
          </label>
          <label className="text-sm">
            Tenant type
            <select
              className="input mt-1 w-full"
              value={form.tenant_type}
              onChange={e => change('tenant_type', e.target.value)}
            >
              <option value="solo_ca">Solo CA</option>
              <option value="medium_firm">Medium firm</option>
              <option value="large_firm">Large firm</option>
            </select>
          </label>
          <label className="text-sm">
            Plan
            <select
              className="input mt-1 w-full"
              value={form.plan_code}
              onChange={e => change('plan_code', e.target.value)}
            >
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
              <option value="trial">Trial</option>
            </select>
          </label>
          <label className="text-sm">
            Owner name
            <input
              required
              minLength={2}
              className="input mt-1 w-full"
              value={form.owner_name}
              onChange={e => change('owner_name', e.target.value)}
            />
          </label>
          <label className="text-sm">
            Owner email
            <input
              required
              type="email"
              className="input mt-1 w-full"
              value={form.owner_email}
              onChange={e => change('owner_email', e.target.value)}
            />
          </label>
          <div
            role="note"
            className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 md:col-span-2"
          >
            This submits a pending registration. The owner verifies their email and chooses their
            password and authenticator. Trial time begins after firm activation.
          </div>
          <label className="text-sm">
            Firm email
            <input
              type="email"
              className="input mt-1 w-full"
              value={form.firm_email ?? ''}
              onChange={e => change('firm_email', e.target.value)}
            />
          </label>
          <label className="text-sm">
            Firm phone
            <input
              type="tel"
              pattern="^[6-9]\d{9}$"
              title="10-digit Indian mobile number starting with 6-9 (e.g. 9876543210)"
              placeholder="10-digit mobile (e.g. 9876543210)"
              className="input mt-1 w-full"
              value={form.firm_phone ?? ''}
              onChange={e => change('firm_phone', e.target.value)}
            />
          </label>
        </fieldset>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button disabled={saving} className="btn btn-primary">
            {saving
              ? 'Requesting…'
              : pending.current
                ? 'Retry original registration'
                : 'Create tenant'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export function EditTenantDialog({
  tenant,
  onClose,
  onSave,
}: {
  tenant: PlatformTenant
  onClose: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const [name, setName] = useState(tenant.tenant_name)
  const [type, setType] = useState(tenant.tenant_type)
  const [status, setStatus] = useState(tenant.status)
  const [email, setEmail] = useState(tenant.firm_email ?? '')
  const [phone, setPhone] = useState(tenant.firm_phone ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await onSave({
        tenant_name: name,
        tenant_type: type,
        status,
        firm_email: email || null,
        firm_phone: phone || null,
      })
      onClose()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Tenant update failed')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog
      title="Edit tenant"
      description={`${tenant.tenant_name} · ${tenant.tenant_code}`}
      onClose={onClose}
    >
      <form className="grid gap-4 md:grid-cols-2" onSubmit={event => void submit(event)}>
        <ErrorMessage error={error} />
        <label className="text-sm md:col-span-2">
          Organization name
          <input
            required
            minLength={2}
            className="input mt-1 w-full"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </label>
        <label className="text-sm">
          Tenant type
          <select
            className="input mt-1 w-full"
            value={type}
            onChange={e => setType(e.target.value)}
          >
            <option value="solo_ca">Solo CA</option>
            <option value="medium_firm">Medium firm</option>
            <option value="large_firm">Large firm</option>
          </select>
        </label>
        <label className="text-sm">
          Status
          <select
            className="input mt-1 w-full"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
        </label>
        <label className="text-sm">
          Firm email
          <input
            type="email"
            className="input mt-1 w-full"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </label>
        <label className="text-sm">
          Firm phone
          <input
            className="input mt-1 w-full"
            value={phone}
            onChange={e => setPhone(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2 md:col-span-2">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button disabled={saving} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

const platformRoles = new Set<CanonicalUserRole>(['cml_admin', 'cml_monitor', 'cml_operations'])

export function CreateUserDialog({
  tenants,
  onClose,
  onCreate,
}: {
  tenants: PlatformTenant[]
  onClose: () => void
  onCreate: (body: CreatePlatformUser) => Promise<void>
}) {
  const [scope, setScope] = useState<'tenant' | 'platform'>(
    tenants.length > 0 ? 'tenant' : 'platform'
  )
  const [form, setForm] = useState<CreatePlatformUser>({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: tenants.length > 0 ? 'firm_admin' : 'cml_admin',
    tenant_id: tenants[0]?.tenant_id,
    is_verified: true,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const availableRoles =
    scope === 'tenant'
      ? CANONICAL_ROLES.filter(r => !platformRoles.has(r))
      : CANONICAL_ROLES.filter(r => platformRoles.has(r))

  const handleScopeChange = (newScope: 'tenant' | 'platform') => {
    setScope(newScope)
    if (newScope === 'tenant') {
      setForm(curr => ({
        ...curr,
        role: 'firm_admin',
        tenant_id: curr.tenant_id || tenants[0]?.tenant_id,
      }))
    } else {
      setForm(curr => ({
        ...curr,
        role: 'cml_admin',
        tenant_id: undefined,
      }))
    }
  }

  const change = (key: keyof CreatePlatformUser, value: string | boolean | undefined) =>
    setForm(current => ({ ...current, [key]: value }))

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await onCreate({ ...form, tenant_id: scope === 'tenant' ? form.tenant_id : undefined })
      onClose()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'User creation failed')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog
      title="Create user"
      description="Create a platform identity or an account assigned to a tenant."
      onClose={onClose}
    >
      <form className="grid gap-4 md:grid-cols-2" onSubmit={event => void submit(event)}>
        <ErrorMessage error={error} />
        <div className="md:col-span-2">
          <span className="text-sm font-medium text-text-primary block mb-1.5">Identity Scope</span>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="identity_scope"
                value="tenant"
                checked={scope === 'tenant'}
                onChange={() => handleScopeChange('tenant')}
              />
              <span>Tenant User (CA Firm)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="identity_scope"
                value="platform"
                checked={scope === 'platform'}
                onChange={() => handleScopeChange('platform')}
              />
              <span>Platform Admin (CML Identity)</span>
            </label>
          </div>
        </div>
        <label className="text-sm">
          Username
          <input
            required
            minLength={3}
            pattern="[A-Za-z0-9_.]+"
            className="input mt-1 w-full"
            value={form.username}
            onChange={e => change('username', e.target.value)}
          />
        </label>
        <label className="text-sm">
          Full name
          <input
            className="input mt-1 w-full"
            value={form.full_name ?? ''}
            onChange={e => change('full_name', e.target.value)}
          />
        </label>
        <label className="text-sm md:col-span-2">
          Email
          <input
            required
            type="email"
            className="input mt-1 w-full"
            value={form.email}
            onChange={e => change('email', e.target.value)}
          />
        </label>
        <label className="text-sm">
          Role
          <select
            className="input mt-1 w-full"
            value={form.role}
            onChange={e => change('role', e.target.value)}
          >
            {availableRoles.map(item => (
              <option value={item} key={item}>
                {getRoleLabel(item)}
              </option>
            ))}
          </select>
        </label>
        {scope === 'tenant' ? (
          <label className="text-sm">
            Tenant <span className="text-red-500">*</span>
            <select
              required
              className="input mt-1 w-full"
              value={form.tenant_id ?? ''}
              onChange={e => change('tenant_id', e.target.value)}
            >
              <option value="">Select tenant</option>
              {tenants.map(tenant => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.tenant_name} ({tenant.tenant_code})
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="text-sm flex flex-col justify-end text-text-tertiary">
            <span className="text-xs">Platform users have tenantless system access.</span>
          </div>
        )}
        <div className="md:col-span-2">
          <PasswordField
            label="Temporary password"
            required
            minLength={8}
            autoComplete="new-password"
            value={form.password}
            onChange={val => change('password', val)}
            showRequirements={true}
            helperText="Minimum 8 characters. Share it securely with the user."
          />
        </div>
        <label className="flex items-center gap-2 text-sm md:col-span-2">
          <input
            type="checkbox"
            checked={form.is_verified}
            onChange={e => change('is_verified', e.target.checked)}
          />
          Mark email as verified
        </label>
        <div className="flex justify-end gap-2 md:col-span-2">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            disabled={saving || (scope === 'tenant' && !form.tenant_id)}
            className="btn btn-primary"
          >
            {saving ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

export function EditUserDialog({
  user,
  onClose,
  onSave,
}: {
  user: PlatformUser
  onClose: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const [name, setName] = useState(user.full_name ?? '')
  const [email, setEmail] = useState(user.email ?? '')
  const [verified, setVerified] = useState(user.is_verified)
  const [nameChanged, setNameChanged] = useState(false)
  const [emailChanged, setEmailChanged] = useState(false)
  const [verifiedChanged, setVerifiedChanged] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await onSave({
        ...(nameChanged ? { full_name: name || null } : {}),
        ...(emailChanged ? { email } : {}),
        ...(verifiedChanged ? { is_verified: verified } : {}),
      })
      onClose()
    } catch (value) {
      setError(value instanceof Error ? value.message : 'User update failed')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog title="Edit user" description={user.username ?? user.id} onClose={onClose}>
      <form className="space-y-4" onSubmit={event => void submit(event)}>
        <ErrorMessage error={error} />
        <label className="block text-sm">
          Full name
          <input
            className="input mt-1 w-full"
            value={name}
            onChange={e => {
              setName(e.target.value)
              setNameChanged(true)
            }}
          />
        </label>
        <label className="block text-sm">
          Email
          <input
            required={emailChanged}
            type="email"
            className="input mt-1 w-full"
            value={email}
            onChange={e => {
              setEmail(e.target.value)
              setEmailChanged(true)
            }}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={verified === true}
            onChange={e => {
              setVerified(e.target.checked)
              setVerifiedChanged(true)
            }}
          />
          Email verified
        </label>
        {verified == null && !verifiedChanged && (
          <p className="text-sm text-text-tertiary">Verification: Not recorded</p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button disabled={saving} className="btn btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
